<template>
  <div class="mx-10 ma-5">
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>
    <v-card flat class="pa-5">
      <div>
        <PageTitleWithSearch
          :title="'PO'"
          :btnName="'Create Purchase Order'"
          :btnColor="'#00B3FF'"
          :btnRoute="'/purchase-orders/create'"
          :btnPermission="'purchase_order.create'"
          :btnEnable="true"
          :loading="loading"
          :search="search"
          @searchText="searchChange($event)"
        ></PageTitleWithSearch>

        <!-- Data Display -->
        <DataTableWithPagination
          :headers="tableHeaders"
          :items="purchaseOrders"
          :loading="loading"
          :page="page"
          :total="total"
          @pageChange="page = $event"
        >
          <template #row="{ item }">
            <tr
              @click="
                hasPermission('purchase_order.view') && goToItemList(item)
              "
            >
              <td>{{ item.po_number }}</td>
              <td>{{ item.requisition_number }}</td>
              <td class="text-left">{{ item.supplier_name }}</td>
              <td class="text-left">
                {{ formatDate(item.po_date, dateFormat) }}
              </td>
              <td class="text-left">{{ item.grand_total }}</td>
              <td class="text-left" v-if="item.status">
                <v-chip
                  small
                  class=""
                  dark
                  :color="getStatusColor(item.status)"
                >
                  {{ stringFormatting(item.status) }}
                </v-chip>
              </td>
              <td class="text-left">
                <v-btn
                  v-if="hasPermission('purchase_order.edit')"
                  class="ma-1"
                  color="#FFE0B2"
                  :to="'/purchase-orders/edit/' + item.uuid"
                  fab
                  x-small
                  style="box-shadow: none;"
                >
                  <v-icon small color="#FF9800">mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  class="ma-1"
                  disabled
                  fab
                  x-small
                  style="box-shadow: none;"
                  ><v-icon small color="#FF9800">mdi-pencil</v-icon></v-btn
                >
                <v-btn
                  v-if="hasPermission('purchase_order.delete')"
                  class="my-1"
                  x-small
                  color="#FFCDD2"
                  @click.stop="dropMemberConfirm(item.uuid)"
                  fab
                  style="box-shadow: none;"
                >
                  <v-icon dark small color="#F44336">mdi-delete</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  class="my-1"
                  disabled
                  fab
                  x-small
                  style="box-shadow: none;"
                >
                  <v-icon small color="#FF9800">mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </DataTableWithPagination>
      </div>
    </v-card>
    <ConfirmModal
      :text="'Do you want to drop this PO?'"
      :title="'Drop PO'"
      :color="'red'"
      :confirmOpen="confirmOpen"
      @cancelConfirm="cancelConfirm()"
      @confirm="confirm()"
    ></ConfirmModal>
    <Snackbars
      :text="successMsg"
      :snackbar="snackbar"
      :color="'warning'"
      @closeSnackbar="closeSnackbar"
    />
  </div>
</template>

<script>
import { PurchaseOrderService } from "@/services/purchaseorder-service.js";
import PageTitleWithSearch from "@/components/shared/PageTitleWithSearch";
import { checkError } from "@/config/errors.js";
import ConfirmModal from "@/components/shared/ConfirmModal";
import Snackbars from "@/components/shared/Snackbars";
import DataTableWithPagination from "@/components/shared/DataTableWithPagination.vue";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");
import Alert from "@/components/shared/Alert.vue";
const { mapGetters: mapSettingsGetters } = createNamespacedHelpers("settings");
import { useDateFormatter } from "../../composables/useDateFormatter.js";

export default {
  components: {
    PageTitleWithSearch,
    ConfirmModal,
    Snackbars,
    DataTableWithPagination,
    Alert,
  },
  data() {
    return {
      dropId: null,
      confirmOpen: false,
      loading: true,
      purchaseOrders: [],
      errors: null,
      snackbar: false,
      successMsg: "",
      search: "",
      page: 1,
      total: 0,
      headers: [
        { text: "PO Number", value: "po_number" },
        { text: "Requisition Number", value: "requisition_number" },
        { text: "Supplier", value: "supplier.name" },
        { text: "PO Date", value: "po_date" },
        { text: "Grand total", value: "grand_total", isCurrency: true },
        { text: "Status", value: "status" },
        { text: "Actions" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
    ...mapSettingsGetters({
      dateFormat: "dateFormat",
      currencyCode: "currencyCode",
    }),
    tableHeaders() {
      return this.headers.map((h) => {
        if (h.isCurrency) {
          return {
            ...h,
            text: `${h.text} (${this.currencyCode})`, // Add currency code automatically
          };
        }
        return h;
      });
    },
  },
  created() {
    this.fetchPurchaseOrders();
  },
  setup() {
    const { formatDate } = useDateFormatter();
    return {
      formatDate,
    };
  },
  watch: {
    search() {
      this.fetchPurchaseOrders();
    },
    page() {
      this.fetchPurchaseOrders();
    },
  },
  methods: {
    async fetchPurchaseOrders() {
      this.loading = true;
      try {
        const response = await PurchaseOrderService.getPurchaseOrders(
          this.search,
          this.page
        );
        this.purchaseOrders = response.data?.data || [];
        this.total = response.data?.meta?.last_page || 0;
      } catch (error) {
        this.purchaseOrders = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    goToItemList(item) {
      this.$router.push({
        name: "po-details",
        params: { uuid: item.uuid, po_number: item.po_number },
      });
      // this.$router.push({ name: "po-details", query: { uuid: item.uuid, po_number: item.po_number } });
      // this.$router.push({ name: "po-details", query: { item: item} });
    },
    getStatusColor(status) {
      switch (status) {
        case "approved":
          return "green";
        case "pending":
          return "orange";
        case "cancelled":
          return "red";
        default:
          return "grey";
      }
    },
    stringFormatting(status) {
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    searchChange(value) {
      this.search = value;
    },

    dropMemberConfirm(uuid) {
      this.dropId = uuid;
      this.confirmOpen = true;
    },

    cancelConfirm() {
      this.confirmOpen = false;
    },

    confirm() {
      this.confirmOpen = false;
      this.dropMember();
    },

    dropMember() {
      if (this.dropId) {
        this.loading = true;
        PurchaseOrderService.dropMember(this.dropId)
          .then((response) => {
            console.log(response);
            this.purchaseOrders = this.purchaseOrders.filter(
              (order) => order.uuid !== this.dropId
            );
            this.snackbar = true;
            this.successMsg = "Purchase Order deleted successfully.";
          })
          .catch((error) => {
            let data = error.response?.data?.error || "Unknown error";

            let errorOutput = checkError.errorFormatting(data);
            let errorsArray = [];
            if (Array.isArray(errorOutput)) {
              errorsArray = errorOutput.filter((e) => !!e && e.trim());
              if (errorsArray.length === 0) errorsArray = [data];
            } else if (errorOutput && errorOutput.trim()) {
              errorsArray = [errorOutput];
            } else {
              errorsArray = [data];
            }
            this.errors = errorsArray;
          })
          .finally(() => {
            this.loading = false;
            this.dropId = null;
          });
      }
    },
    closeSnackbar() {
      this.snackbar = false;
    },
    onDismissed() {
      this.errors = null;
    },
  },
};
</script>
